import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import Input from './Input';
import BuildBaseIcon from '../Icons/BuildBaseIcon';
import PlusSubmitIcon from '../Icons/PlusSubmitIcon';
import { getFooterContent } from '../api-sanity/api';
import useMediaQuery from 'hooks/useMediaQuery';

const Footer = ({ isDarkMode = false }) => {
  const [footerData, setFooterData] = useState(null);
  const isMobileSize = useMediaQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const isContactPage = location.pathname === '/contact';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState('idle');
  const isSubmitting = submitStatus === 'loading';
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    company: false,
    phone: false,
  });

  useEffect(() => {
    const fetchFooterData = async () => {
      const data = await getFooterContent();
      if (!data.error) {
        setFooterData(data);
      }
    };
    fetchFooterData();
  }, []);

  const handleLinkClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    const withPlus = trimmed.startsWith('00')
      ? `+${trimmed.slice(2)}`
      : trimmed;
    const cleaned = withPlus.replace(/(?!^)[^\d]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (/^\d+$/.test(cleaned)) return `+${cleaned}`;
    return cleaned;
  };

  const isE164 = (value) => {
    return /^\+[1-9]\d{7,14}$/.test(value);
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    const normalized = normalizePhone(phone);
    return isE164(normalized);
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !validateEmail(formData.email),
      company: !formData.company.trim(),
      phone: !validatePhone(formData.phone),
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('loading');

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append(
      'Authorization',
      'Bearer skNeRLxHT0sGe9HaThzwVRYv8MMzFqQq9GlV9GhYD7LKKqLtMFJpT1AgZ0bYAa460uMMIjLs8cqnBjOMUxamTflqRDuBbjU4eisDp8TWTr1zpsOS5JjxupZns4h6xHZ3kvXiYqnDacG0ymlMm19SlIWH9AlGIXM7MUdOHY167ln0Pz4XAPrw',
    );

    const raw = JSON.stringify({
      mutations: [
        {
          create: {
            ...formData,
            _id: Math.floor(Date.now() / 1000).toString(),
            _type: 'contact',
            submissionDate: new Date().toISOString(),
          },
        },
      ],
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    };

    fetch(
      'https://kxxtoumf.api.sanity.io/v2025-05-12/data/mutate/production',
      requestOptions,
    )
      .then((response) => response.text())
      .then(() => {
        setSubmitStatus('success');

        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            message: '',
          });
          setValidationErrors({
            name: false,
            email: false,
            company: false,
            phone: false,
          });
          setSubmitStatus('idle');
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
        setSubmitStatus('idle');
      });
  };

  return (
    <div
      className={`footer-container ${isDarkMode ? 'dark' : ''} ${
        isContactPage ? 'footer-container-compact' : ''
      }`}
    >
      {!isContactPage && (
        <div className='flex footer-contact-row'>
          {!isMobileSize && (
            <BuildBaseIcon
              onClick={() => navigate('/')}
              className='footer-contact-logo cursor-pointer align-self-start'
            />
          )}
          {isMobileSize && (
            <p className='balanced-text uppercase full-width sec-nd-hp-p font-primary'>
              <span>Wie kann ich </span>
              <span className='font-secondary non-active'>sie Erreichen?</span>
            </p>
          )}
          <form className='flex footer-contact-form' onSubmit={handleSubmit}>
            <Input
              placeholder='Name*'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              className={`${validationErrors.name ? 'validation-error' : ''} ${
                isSubmitting ? 'loading' : ''
              }`}
            />
            <Input
              placeholder='E-Mail*'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              className={`${
                validationErrors.email ? 'validation-error' : ''
              } ${isSubmitting ? 'loading' : ''}`}
            />
            <Input
              placeholder='Firmenname*'
              name='company'
              value={formData.company}
              onChange={handleInputChange}
              className={`${
                validationErrors.company ? 'validation-error' : ''
              } ${isSubmitting ? 'loading' : ''}`}
            />
            <Input
              type='tel'
              inputMode='tel'
              autoComplete='tel'
              placeholder='Phone (e.g., +49 15123456789)'
              name='phone'
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={(e) => {
                const normalized = normalizePhone(e.target.value);
                setFormData((prev) => ({ ...prev, phone: normalized }));
              }}
              pattern='^\+[1-9]\d{7,14}$'
              maxLength='16'
              title='Enter a valid number in E.164 format, e.g., +4915123456789'
              className={`${
                validationErrors.phone ? 'validation-error' : ''
              } ${isSubmitting ? 'loading' : ''}`}
            />
            <textarea
              placeholder='Hinterlassen Sie eine Nachricht'
              name='message'
              value={formData.message}
              onChange={handleInputChange}
              maxLength='2000'
              className={isSubmitting ? 'loading' : ''}
            />
            <Button
              className='flex justify-center'
              showIcon={true}
              customIcon={<PlusSubmitIcon status={submitStatus} />}
              type='submit'
              disabled={submitStatus !== 'idle'}
            >
              {submitStatus === 'success'
                ? 'Wir melden uns in kürze bei Ihnen.'
                : 'Absenden'}
            </Button>
          </form>
        </div>
      )}
      <div className='flex footer-links-row'>
        <div className='flex footer-links-wrapper '>
          <div className='flex footer-links'>
            {!isMobileSize && (
              <div>
                <span className='ft-tile'>HOME</span>
                <span
                  className='ft-btn'
                  onClick={() => handleLinkClick(footerData?.projectUrl)}
                >
                  PROJECT
                </span>
                <span
                  className='ft-btn'
                  onClick={() => handleLinkClick(footerData?.aboutUrl)}
                >
                  ABOUT
                </span>
                <span className='ft-btn' onClick={() => navigate('/contact')}>
                  CONTACT
                </span>
              </div>
            )}

            <div>
              <span className='ft-tile'>SOCIALS</span>
              <span
                className='ft-btn'
                onClick={() => handleLinkClick(footerData?.instagramUrl)}
              >
                INSTAGRAM
              </span>
              <span
                className='ft-btn'
                onClick={() => handleLinkClick(footerData?.linkedinUrl)}
              >
                LINKEDIN
              </span>
              <span
                className='ft-btn'
                onClick={() => handleLinkClick(footerData?.twitterUrl)}
              >
                TWITTER
              </span>
            </div>
            <div>
              <span className='ft-tile'>INFO</span>
              <span>SWITZERLAND</span>
              <span>BASEL</span>
              <span>INFO@BUILD:BASE.CH</span>
            </div>
          </div>
        </div>
        <div className='flex'>
          {isMobileSize && (
            <div className='logo-wrapper'>
              <BuildBaseIcon
                width='100%'
                height='100%'
                onClick={() => navigate('/')}
                className='cursor-pointer'
              />
            </div>
          )}
          <span className='footer-copyright'>
            © 2025 Build:base. All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Footer;
